import { Link } from "../lib/router";

const rows = [
  ["Primary product", "Hosted social workspace plus REST API", "API infrastructure embedded into another product"],
  ["Published networks", "9 social platforms", "13+ social networks"],
  ["User interface", "Dashboard for publishing, accounts, and analytics", "No Ayrshare end-user interface; customers build their own"],
  ["Publishing", "Multi-network publishing with platform overrides", "Single API for publishing across supported networks"],
  ["Scheduling", "Scheduling and queue workflow", "Unlimited scheduled posts on current paid plans"],
  ["Analytics", "Unified post and page analytics", "Publishing, history, analytics, moderation, and engagement APIs"],
  ["Account model", "Social accounts grouped into SocialCast projects", "Active social profiles, each spanning multiple networks"],
  ["Published entry price", "Free plan; Pro is $19/month", "Premium is $149/month; multi-profile Launch is $299/month"],
  ["Best fit", "Creators, lean teams, agencies, and developers wanting a ready workspace", "Products needing broad white-label API infrastructure at scale"],
];

export function AyrshareComparison() {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="max-w-3xl mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold border border-orange-200/60 dark:border-orange-500/20 mb-6">
            Social media API comparison
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white tracking-tight leading-tight mb-5">
            SocialCast vs Ayrshare
          </h1>
          <p className="text-lg text-stone-500 dark:text-zinc-400 leading-8">
            Ayrshare is a mature, broad API infrastructure product. SocialCast combines API access with a ready-to-use
            publishing workspace at a lower starting price. The right choice depends on whether you are embedding social
            features into your own product or want the product experience included.
          </p>
          <p className="text-sm text-stone-400 dark:text-zinc-500 mt-5">Vendor-authored comparison. Last verified July 13, 2026.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-14">
          <Decision
            title="Choose SocialCast when"
            items={[
              "You want a usable social dashboard and an API without building the interface first.",
              "A low-cost starting point matters for a creator, small team, agency, or early product.",
              "Nine supported networks cover your publishing roadmap.",
            ]}
          />
          <Decision
            title="Choose Ayrshare when"
            items={[
              "You are embedding social capabilities inside a multi-tenant SaaS product.",
              "Thirteen-plus networks, moderation, messaging, and broad engagement APIs are required.",
              "A profile-based infrastructure model and established high-volume operation justify the price.",
            ]}
          />
        </div>

        <h2 className="text-3xl font-extrabold text-stone-900 dark:text-white mb-6">Feature-by-feature comparison</h2>
        <div className="overflow-x-auto rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 mb-14">
          <table className="w-full min-w-[780px] text-sm bg-white dark:bg-zinc-900">
            <thead className="bg-stone-50 dark:bg-zinc-900">
              <tr>
                <th className="p-4 text-left font-semibold text-stone-900 dark:text-white">Area</th>
                <th className="p-4 text-left font-semibold text-orange-600 dark:text-orange-400">SocialCast</th>
                <th className="p-4 text-left font-semibold text-stone-900 dark:text-white">Ayrshare</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-zinc-800">
              {rows.map(([area, socialCast, ayrshare]) => (
                <tr key={area}>
                  <th className="p-4 text-left align-top font-semibold text-stone-900 dark:text-white">{area}</th>
                  <td className="p-4 align-top text-stone-600 dark:text-zinc-300 leading-6">{socialCast}</td>
                  <td className="p-4 align-top text-stone-600 dark:text-zinc-300 leading-6">{ayrshare}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
          <Detail title="Product boundary">
            Ayrshare intentionally stays behind your product. SocialCast includes the customer-facing workspace, which
            reduces implementation work but offers less freedom than a fully custom interface.
          </Detail>
          <Detail title="Pricing boundary">
            Compare costs at your expected customer count. Ayrshare bills active profiles and scales down per profile.
            SocialCast publishes account and project limits with API access on Pro.
          </Detail>
          <Detail title="Evaluation test">
            Connect LinkedIn and Instagram, publish mixed media, schedule a post, fetch analytics, and model ten customer
            profiles. Measure both API effort and the interface work still required.
          </Detail>
        </div>

        <div className="border-y border-stone-200/60 dark:border-zinc-800/60 py-8 mb-12">
          <h2 className="text-xl font-bold text-stone-900 dark:text-white mb-3">Sources and methodology</h2>
          <p className="text-stone-600 dark:text-zinc-300 leading-7 mb-3">
            Ayrshare capabilities and prices were checked against its official product, API, and pricing pages. SocialCast
            details reflect the current product pages and published plans. Verify current pricing before purchase.
          </p>
          <div className="flex flex-wrap gap-5 text-sm">
            <a className="text-orange-600 dark:text-orange-400 underline underline-offset-4" href="https://www.ayrshare.com/" target="_blank" rel="noreferrer">Ayrshare product</a>
            <a className="text-orange-600 dark:text-orange-400 underline underline-offset-4" href="https://www.ayrshare.com/pricing/" target="_blank" rel="noreferrer">Ayrshare pricing</a>
            <Link className="text-orange-600 dark:text-orange-400 underline underline-offset-4" href="/features">SocialCast features</Link>
            <Link className="text-orange-600 dark:text-orange-400 underline underline-offset-4" href="/pricing">SocialCast pricing</Link>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-3xl font-extrabold text-stone-900 dark:text-white mb-2">Test the complete workflow</h2>
            <p className="text-stone-500 dark:text-zinc-400">Connect accounts, publish, schedule, and inspect analytics before committing.</p>
          </div>
          <a href="https://app.socialcast.dev/register" className="inline-flex justify-center px-6 py-3 rounded-xl bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap">
            Get Started
          </a>
        </div>
      </div>
    </section>
  );
}

function Decision({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="border-t-2 border-orange-500 pt-5">
      <h2 className="text-2xl font-bold text-stone-900 dark:text-white mb-4">{title}</h2>
      <ul className="space-y-3 text-stone-600 dark:text-zinc-300">
        {items.map((item) => <li key={item} className="leading-7">{item}</li>)}
      </ul>
    </div>
  );
}

function Detail({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl font-bold text-stone-900 dark:text-white mb-3">{title}</h2>
      <p className="text-stone-600 dark:text-zinc-300 leading-7">{children}</p>
    </div>
  );
}
